/** @format — Trucking payment APIs via AUTH_BASE_URL (dev.dockie.ai/api) */

import { authClient } from "./api";
import { ENDPOINTS } from "./constants";
import { AUTH_BASE_URL, DEBUG_PAYMENT_PROVIDER } from "@/config";
import { getToken } from "@/utils/tokenStorage";

const DEBUG_PAYMENT_PROVIDER_STORAGE_KEY = "dockie_debug_payment_provider";

const withBearerAuth = (config = {}) => {
  const token = getToken();

  return {
    ...config,
    headers: {
      ...(config.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  };
};

const getDebugPaymentProvider = () => {
  const envProvider = DEBUG_PAYMENT_PROVIDER?.trim()?.toLowerCase();
  const storageProvider =
    typeof window !== "undefined"
      ? window.localStorage.getItem(DEBUG_PAYMENT_PROVIDER_STORAGE_KEY)?.trim()?.toLowerCase()
      : null;

  const debugProvider = storageProvider || envProvider;

  if (process.env.NODE_ENV === "production") {
    return null;
  }

  return debugProvider === "stripe" || debugProvider === "paystack" ? debugProvider : null;
};

const withTruckingPaymentAuth = (config = {}) => {
  const debugProvider = getDebugPaymentProvider();

  return withBearerAuth({
    ...config,
    headers: {
      ...(config.headers || {}),
      ...(debugProvider ? { "X-Debug-Payment-Provider": debugProvider } : {}),
    },
  });
};

const getApiOrigin = () => {
  try {
    return new URL(AUTH_BASE_URL).origin;
  } catch {
    return "";
  }
};

const buildTruckingUrl = (path) => {
  const apiOrigin = getApiOrigin();
  return apiOrigin ? `${apiOrigin}${path}` : path;
};

export const paymentService = {
  createPaymentQuote: async (payload) => {
    const response = await authClient.post(
      buildTruckingUrl(ENDPOINTS.PAYMENT.PAYMENT_QUOTE),
      payload,
      withTruckingPaymentAuth(),
    );
    return response.data;
  },

  createStripeCheckoutSession: async ({ sessionId, returnUrl, cancelUrl }) => {
    const response = await authClient.post(
      buildTruckingUrl(ENDPOINTS.PAYMENT.STRIPE_CHECKOUT_SESSION),
      { sessionId, returnUrl, cancelUrl },
      withTruckingPaymentAuth(),
    );
    return response.data;
  },

  getPaymentStatus: async (sessionId) => {
    const response = await authClient.get(
      buildTruckingUrl(`${ENDPOINTS.PAYMENT.PAYMENT_STATUS}/${sessionId}/`),
      withTruckingPaymentAuth(),
    );
    return response.data;
  },

  cancelPaymentStatus: async (sessionId) => {
    const response = await authClient.post(
      buildTruckingUrl(`${ENDPOINTS.PAYMENT.PAYMENT_STATUS}/${sessionId}/cancel/`),
      {},
      withTruckingPaymentAuth(),
    );
    return response.data;
  },

  logPaymentInit: async ({ reference, sessionId }) => {
    const response = await authClient.post(
      buildTruckingUrl(ENDPOINTS.PAYMENT.LOG_PAYMENT_INIT),
      { reference, sessionId },
      withTruckingPaymentAuth(),
    );
    return response.data;
  },

  verifyTruckingPayment: async ({ reference, sessionId }) => {
    const response = await authClient.post(
      buildTruckingUrl(`${ENDPOINTS.PAYMENT.VERIFY_TRUCKING_PAYMENT}/${reference}/`),
      { sessionId },
      withTruckingPaymentAuth(),
    );
    return response.data;
  },

  /** Legacy Paystack verify on main auth API */
  verifyPayment: async (reference, session_id, owner) => {
    const response = await authClient.post(`${ENDPOINTS.PAYMENT.VERIFY_PAYSTACK_PAYMENT}/${reference}`, {
      session_id,
      owner,
    });
    return response.data;
  },
};
